import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';

export enum GuaranteesFetchStatus {
  Idle = 'idle',
  Loading = 'loading',
  Success = 'success',
  Error = 'error',
}

export type TGuaranteesThunkError = {
  message: string;
  status?: number;
  errorCode?: string;
  data?: unknown;
};

export type TGuaranteesAsyncSlice<T> = {
  data: T;
  status: GuaranteesFetchStatus;
  error: TGuaranteesThunkError | null;
};

export type TGuaranteesFlowType = 'WEB' | 'MANUAL';

export type TCreateGuaranteesApplicationRequest = {
  contragentId: string;
  flowType: TGuaranteesFlowType;
};

export type TFetchGuaranteesApplicationsRequest = {
  contragentId: string;
  page: number;
  size: number;
};

export type TFetchGuaranteesManagerTasksRequest = {
  ids?: string[];
  page: number;
  search?: string;
  size: number;
  sort?: string[];
  status?: GuaranteesApplicationStatus | '';
};

export enum AdministrationRole {
  UAKDO_ADMINISTRATOR = 'UAKDO_ADMINISTRATOR',
  UAKDO_CONTROLLER = 'UAKDO_CONTROLLER',
  USAPOK_ADMINISTRATOR = 'USAPOK_ADMINISTRATOR',
  USAPOK_CONTROLLER = 'USAPOK_CONTROLLER',
  UBTF_ADMINISTRATOR = 'UBTF_ADMINISTRATOR',
  UBTF_CONTROLLER = 'UBTF_CONTROLLER',
  MANAGER = 'MANAGER',
  MANAGER_CONTROLLER = 'MANAGER_CONTROLLER',
  PM_MANAGER = 'PM_MANAGER',
  AM_MANAGER = 'AM_MANAGER',
  ASSET_MANAGER = 'ASSET_MANAGER',
  UO_ADMINISTRATOR = 'UO_ADMINISTRATOR',
  UO_CONTROLLER = 'UO_CONTROLLER',
  UMVN_ADMINISTRATOR = 'UMVN_ADMINISTRATOR',
  UMVN_CONTROLLER = 'UMVN_CONTROLLER',
  BUSINESS_MANAGER = 'BUSINESS_MANAGER',
  COVENANTS_BRANCH_MANAGER = 'COVENANTS_BRANCH_MANAGER',
  CALL_CENTER_OPERATOR = 'CALL_CENTER_OPERATOR',
  PERSONAL_DATA_MANAGER = 'PERSONAL_DATA_MANAGER',
  OBSERVER = 'OBSERVER',
}

export type TGuaranteesAdministrationRolesResponse = {
  roles: AdministrationRole[];
};

export type TFetchGuaranteesAdministrationTasksRequest = {
  adminRoles: AdministrationRole[];
  ids?: string[];
  page: number;
  search?: string;
  size: number;
  sort?: string[];
  status?: GuaranteesApplicationStatus | '';
};

export type TGuaranteesAdministrationTasksResponse = {
  tasks: TGuaranteesApplication[];
  count: number;
  pageConfiguration: TGuaranteesPageConfiguration;
};

export type TUpdateGuaranteesApplicationStatusRequest = {
  applicationId: string;
  applicationStatus: GuaranteesApplicationStatus;
  version: number;
};

export type TUpdateGuaranteesApplicationStatusResponse = {
  id: string;
  version: number;
  managerEmail?: string | null;
  applicationStatus: GuaranteesApplicationStatus | string;
  processingDeadline?: string | null;
  processingOverdue?: boolean | null;
  udbtfProcessingDeadline?: string | null;
  udbtfProcessingOverdue?: boolean | null;
  umvnProcessingDeadline?: string | null;
  umvnProcessingOverdue?: boolean | null;
  uoProcessingDeadline?: string | null;
  uoProcessingOverdue?: boolean | null;
};

export type TGuaranteesAssigneeUnit = 'DEFAULT' | 'UDBTF' | 'UMVN' | 'UO';

export type TFetchGuaranteesTaskSupervisorsRequest = {
  taskId: string;
  unit: TGuaranteesAssigneeUnit;
};

export type TGuaranteesTaskSupervisorsResponse = {
  unit: TGuaranteesAssigneeUnit;
  assignedEmployeeEmail: string | null;
  supervisorEmails: string[];
};

export type TPatchGuaranteesApplicationAssigneeRequest = {
  applicationId: string;
  version: number;
  payload: {
    unit: TGuaranteesAssigneeUnit;
    assignedEmployeeEmail: string | null;
  };
};

export type TPatchGuaranteesApplicationAssigneeResponse = TGuaranteesApplication;

export enum GuaranteesUdbtfApprovalStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  APPROVED = 'APPROVED',
  SKIPPED = 'SKIPPED',
  DENIED = 'DENIED',
}

export enum GuaranteesUoApprovalStatus {
  APPROVED = 'APPROVED',
  IN_PROGRESS = 'IN_PROGRESS',
  PENDING = 'PENDING',
  SKIPPED = 'SKIPPED',
  DENIED = 'DENIED',
}

export enum GuaranteesUmvnApprovalStatus {
  APPROVED = 'APPROVED',
  IN_PROGRESS = 'IN_PROGRESS',
  PENDING = 'PENDING',
  SKIPPED = 'SKIPPED',
  DENIED = 'DENIED',
}

export type TUpdateGuaranteesUdbtfApprovalStatusRequest = {
  applicationId: string;
  applicationApprovalStatus: GuaranteesUdbtfApprovalStatus;
  description?: string;
  version: number;
};

export type TUpdateGuaranteesUoApprovalStatusRequest = {
  applicationId: string;
  applicationApprovalStatus: GuaranteesUoApprovalStatus;
  description?: string;
  version: number;
};

export type TUpdateGuaranteesUmvnApprovalStatusRequest = {
  applicationId: string;
  applicationApprovalStatus: GuaranteesUmvnApprovalStatus;
  description?: string;
  version: number;
};

export type TUpdateGuaranteesUdbtfApprovalStatusResponse =
  | TGuaranteesApplication
  | TUpdateGuaranteesApplicationStatusResponse
  | Record<string, unknown>
  | null;

export type TUpdateGuaranteesUoApprovalStatusResponse =
  | TGuaranteesApplication
  | TUpdateGuaranteesApplicationStatusResponse
  | Record<string, unknown>
  | null;

export type TUpdateGuaranteesUmvnApprovalStatusResponse =
  | TGuaranteesApplication
  | TUpdateGuaranteesApplicationStatusResponse
  | Record<string, unknown>
  | null;

export type TSubmitGuaranteesApplicationByManagerRequest = {
  applicationId: string;
  version: number;
};

export type TSubmitGuaranteesApplicationByManagerResponse =
  | TGuaranteesApplication
  | TUpdateGuaranteesApplicationStatusResponse
  | Record<string, unknown>
  | null;

export type TSubmitGuaranteesApplicationByClientRequest = {
  applicationId: string;
  version: number;
};

export type TSubmitGuaranteesApplicationByClientResponse =
  | TGuaranteesApplication
  | TUpdateGuaranteesApplicationStatusResponse
  | Record<string, unknown>
  | null;

export type TSubmitGuaranteesApprovalLetterByClientRequest = {
  applicationId: string;
  version: number;
};

export type TSubmitGuaranteesApprovalLetterByClientResponse =
  | TGuaranteesApplication
  | TUpdateGuaranteesApplicationStatusResponse
  | Record<string, unknown>
  | null;

export type TSubmitGuaranteesApplicationByUdbtfRequest = {
  applicationId: string;
  version: number;
};

export type TSubmitGuaranteesApplicationByUdbtfResponse =
  | TGuaranteesApplication
  | TUpdateGuaranteesApplicationStatusResponse
  | Record<string, unknown>
  | null;

export type TSubmitGuaranteesApprovalLetterByUdbtfRequest = {
  applicationId: string;
  version: number;
};

export type TSubmitGuaranteesApprovalLetterByUdbtfResponse =
  | TGuaranteesApplication
  | TUpdateGuaranteesApplicationStatusResponse
  | Record<string, unknown>
  | null;

export type TSubmitGuaranteesApprovalLetterByUsapokRequest = {
  applicationId: string;
  version: number;
};

export type TSubmitGuaranteesApprovalLetterByUsapokResponse =
  | TGuaranteesApplication
  | TUpdateGuaranteesApplicationStatusResponse
  | Record<string, unknown>
  | null;

export type TSubmitGuaranteesApplicationByUakdoRequest = {
  applicationId: string;
  version: number;
};

export type TSubmitGuaranteesApplicationByUakdoResponse =
  | TGuaranteesApplication
  | TUpdateGuaranteesApplicationStatusResponse
  | Record<string, unknown>
  | null;

export type TSubmitGuaranteesApplicationByUakdoControllerRequest = {
  applicationId: string;
  version: number;
};

export type TSubmitGuaranteesApplicationByUakdoControllerResponse =
  | TGuaranteesApplication
  | TUpdateGuaranteesApplicationStatusResponse
  | Record<string, unknown>
  | null;

export type TRejectGuaranteesApplicationByUakdoControllerRequest = {
  applicationId: string;
  version: number;
  payload: {
    comment: string;
  };
};

export type TRejectGuaranteesApplicationByUakdoControllerResponse =
  | TGuaranteesApplication
  | TUpdateGuaranteesApplicationStatusResponse
  | Record<string, unknown>
  | null;

export type TGuaranteesIssueGuaranteeDetails = Record<string, unknown> & {
  operationProvidedDate?: string | null;
  operationFinishedDate?: string | null;
  managementAccountingProduct?: string | null;
};

export type TGuaranteesManagementAccounting = Record<string, unknown> & {
  product?: string | null;
  operationProvidedDate?: string | null;
  operationFinishedDate?: string | null;
};

export type TPatchGuaranteesIssueGuaranteePayload = {
  operationProvidedDate: string;
  operationFinishedDate: string | null;
};

export type TPatchGuaranteesIssueGuaranteeRequest = {
  applicationId: string;
  payload: TPatchGuaranteesIssueGuaranteePayload;
  version: number;
};

export type TPatchGuaranteesIssueGuaranteeResponse =
  | TGuaranteesIssueGuaranteeDetails
  | TGuaranteesApplication
  | null;

export enum GuaranteesCoverageType {
  COVERED = 'COVERED',
  UNCOVERED = 'UNCOVERED',
}

export type TSubmitGuaranteesApplicationByAmManagerRequest = {
  applicationId: string;
  coverageType: GuaranteesCoverageType;
  version: number;
};

export type TSubmitGuaranteesApplicationByAmManagerResponse =
  | TGuaranteesApplication
  | TUpdateGuaranteesApplicationStatusResponse
  | Record<string, unknown>
  | null;

export type TSubmitGuaranteesApprovalLetterByAmManagerRequest = {
  applicationId: string;
  version: number;
};

export type TSubmitGuaranteesApprovalLetterByAmManagerResponse =
  | TGuaranteesApplication
  | TUpdateGuaranteesApplicationStatusResponse
  | Record<string, unknown>
  | null;

export type TFetchGuaranteesApplicationTimelineRequest = {
  applicationId: string;
};

export type TFetchGuaranteesApplicationRequest = {
  contragentId: string;
  applicationId: string;
};

export type TFetchGuaranteesPrincipalRequest = {
  contragentId: string;
  applicationId: string;
};

export type TFetchGuaranteesBeneficiaryRequest = {
  contragentId: string;
  applicationId: string;
};

export type TFetchGuaranteesFormRequest = {
  contragentId: string;
  applicationId: string;
};

export type TFetchGuaranteesMainDealConditionsRequest = {
  contragentId: string;
  applicationId: string;
};

export type TFetchGuaranteesBaseRelationsRequest = {
  contragentId: string;
  applicationId: string;
};

export type TFetchGuaranteesManagerTermsRequest = {
  contragentId: string;
  applicationId: string;
};

export type TFetchGuaranteesUdbtfTemplateDocumentRequest = {
  applicationId: string;
};

export type TGuaranteesUdbtfTemplateDocumentResponse = {
  data: Blob;
  filename: string;
  contentType?: string;
};

export type TFetchGuaranteesAmTemplateDocumentRequest = {
  applicationId: string;
};

export type TGuaranteesAmTemplateDocumentResponse = {
  data: Blob;
  filename: string;
  contentType?: string;
};

export type TGuaranteesConclusionTemplateApprovalType = 'uo' | 'umvn';

export type TFetchGuaranteesConclusionTemplateDocumentRequest = {
  applicationId: string;
  approvalType: TGuaranteesConclusionTemplateApprovalType;
};

export type TGuaranteesConclusionTemplateDocumentResponse = {
  data: Blob;
  filename: string;
  contentType?: string;
};

export type TFetchGuaranteesApprovalCommentRequest = {
  applicationId: string;
  approvalType: TGuaranteesConclusionTemplateApprovalType;
};

export type TGenerateGuaranteesConclusionAttachmentRequest = {
  applicationId: string;
  approvalType: TGuaranteesConclusionTemplateApprovalType;
  comment: string;
  version: number;
};

export type TGuaranteesApprovalCommentResponse = {
  id?: string | null;
  applicationId?: string;
  approvalType?: TGuaranteesConclusionTemplateApprovalType;
  version?: number | null;
  comment?: string | null;
};

export type TGenerateGuaranteesConclusionAttachmentResponse =
  | TGuaranteesApplicationFile
  | Record<string, unknown>
  | null;

export enum GuaranteesFileType {
  GUARANTEE_SCAN_COPY = 'GUARANTEE_SCAN_COPY',
  WARRANTY_STATEMENT = 'WARRANTY_STATEMENT',
  CLIENT_FOUNDING_DOCUMENTS_AND_GOVERNANCE_INFO = 'CLIENT_FOUNDING_DOCUMENTS_AND_GOVERNANCE_INFO',
  LETTER_OF_APPROVAL = 'LETTER_OF_APPROVAL',
  LETTER_OF_APPROVAL_CLIENT = 'LETTER_OF_APPROVAL_CLIENT',
  LETTER_OF_APPROVAL_MANUAL_SIGNED = 'LETTER_OF_APPROVAL_MANUAL_SIGNED',
  LETTER_OF_APPROVAL_UO = 'LETTER_OF_APPROVAL_UO',
  LETTER_OF_APPROVAL_UDBTF = 'LETTER_OF_APPROVAL_UDBTF',
  LETTER_OF_APPROVAL_UMVN = 'LETTER_OF_APPROVAL_UMVN',
  ATTACHMENT_LETTER_OF_APPROVAL_UO = 'ATTACHMENT_LETTER_OF_APPROVAL_UO',
  ATTACHMENT_LETTER_OF_APPROVAL_UMVN = 'ATTACHMENT_LETTER_OF_APPROVAL_UMVN',
  AM_OTHER_DOCUMENT = 'AM_OTHER_DOCUMENT',
}

export enum GuaranteesApprovalLetterExecutionCheck {
  ELECTRONIC = 'ELECTRONIC',
  PAPER = 'PAPER',
}

export type TUploadGuaranteesClientDocumentRequest = {
  applicationId: string;
  fileType: GuaranteesFileType;
  multipartFile: File | Blob;
};

export type TUploadGuaranteesClientDocumentResponse = {
  id: string;
  fileName: string;
};

export type TFetchGuaranteesApplicationFilesRequest = {
  applicationId: string;
};

export type TGuaranteesApplicationFile = {
  id?: string | null;
  applicationFileId?: string | null;
  attachmentId?: string | null;
  fileId?: string | null;
  fileSignedId?: string | null;
  applicationId?: string | null;
  description?: string | null;
  fileType?: GuaranteesFileType | (string & {}) | null;
};

export type TFetchGuaranteesApplicationFilesResponse = TGuaranteesApplicationFile[];

export type TGuaranteesApplicationFileType = GuaranteesFileType | (string & {});

export type TGuaranteesApplicationFileCapability = {
  fileType: TGuaranteesApplicationFileType;
  displayName?: string | null;
  source?: string | null;
  isMultipleFiles?: boolean;
  canDownload: boolean;
  canUpload: boolean;
  canRegenerate: boolean;
  canDelete: boolean;
  files?: TGuaranteesApplicationFile[];
};

export type TGuaranteesApplicationFileCapabilitiesResponse = {
  fileTypes: TGuaranteesApplicationFileCapability[];
};

export type TPatchGuaranteesApplicationFileCapabilityPayload = Partial<
  Pick<
    TGuaranteesApplicationFileCapability,
    'canDownload' | 'canUpload' | 'canRegenerate' | 'canDelete'
  >
>;

export type TGuaranteesApplicationFileErrorCode =
  | 'FILE_ACTION_NOT_ALLOWED'
  | 'APPLICATION_FILE_ALREADY_EXISTS_USE_FILE_ID'
  | 'APPLICATION_FILE_NOT_FOUND'
  | 'FILE_TYPE_REGENERATION_NOT_SUPPORTED';

export type TUploadGuaranteesApplicationFileRequest = {
  applicationId: string;
  fileType: TGuaranteesApplicationFileType;
  multipartFile: File | Blob;
  fileId?: string;
};

export type TUploadGuaranteesApplicationFileResponse = TGuaranteesApplicationFile;

export type TRegenerateGuaranteesApplicationFileRequest = {
  applicationId: string;
  fileType: TGuaranteesApplicationFileType;
  fileId?: string;
};

export type TRegenerateGuaranteesApplicationFileResponse = TGuaranteesApplicationFile;

export type TPatchGuaranteesApplicationFileCapabilitiesRequest = {
  fileType: TGuaranteesApplicationFileType;
  payload: TPatchGuaranteesApplicationFileCapabilityPayload;
};

export type TPatchGuaranteesApplicationFileCapabilitiesResponse =
  TGuaranteesApplicationFileCapability;

export type TDownloadGuaranteesApplicationFileRequest = {
  applicationId: string;
  fileType: TGuaranteesApplicationFileType;
  fileId: string;
  isAttached?: boolean;
};

export type TDownloadGuaranteesApplicationFileResponse =
  TDownloadGuaranteesClientDocumentResponse;

export type TFetchGuaranteesApplicationFileCapabilitiesRequest = {
  applicationId: string;
};

export type TDeleteGuaranteesApplicationFileRequest = {
  applicationId: string;
  fileType: TGuaranteesApplicationFileType;
  fileId: string;
};

export type TDeleteGuaranteesClientDocumentRequest = {
  applicationFileId: string;
};

export type TDownloadGuaranteesClientDocumentRequest = {
  id: string;
  isAttached: boolean;
};

export type TDownloadGuaranteesClientDocumentResponse = {
  data: Blob;
  filename: string;
  contentType?: string;
};

export type TFilesSigningFileIdsRequest = {
  fileIds: string[];
};
export type TFilesSigningBaseRequest = TFilesSigningFileIdsRequest & {
  applicationId: string;
};

export type TFilesSigningGetHashesRequest = TFilesSigningFileIdsRequest;

export type TFilesSigningDiiaInitializeRequest = TFilesSigningBaseRequest;

export type TFilesSigningDiiaStatusRequest = {
  applicationId: string;
  requestId: string;
};

export type TFilesSigningDiiaApplyRequest = {
  applicationId: string;
  requestId: string;
};

export type TFilesSigningSaveSignedDocumentsRequest = {
  fileList: import('~/store/iit/types.ts').TIITSign[];
  applicationId: string;
};

export type TFilesSigningHash = {
  fileHashToSign: string;
  fileNameToSign: string;
  documentId: number | string;
};

export type TFilesSigningGetHashesResponse = {
  documentsToSign: TFilesSigningHash[];
};

export type TFilesSigningSavedDocument = {
  id: string;
  fileName: string;
};

export type TFilesSigningSaveSignedDocumentsRawResponse =
  | TFilesSigningSavedDocument[]
  | {
      documentsToSign?: TFilesSigningHash[];
      unSavedDocuments?: import('~/store/iit/types.ts').TSavedDocuments[];
      uploadedDocuments?: import('~/store/iit/types.ts').TSavedDocuments[];
      version?: number;
    };

export type TFilesSigningSaveSignedDocumentsResponse = {
  documentsToSign?: TFilesSigningHash[];
  unSavedDocuments?: import('~/store/iit/types.ts').TSavedDocuments[];
  uploadedDocuments?: import('~/store/iit/types.ts').TSavedDocuments[];
  version?: number;
};

export type TFilesSigningDiiaInitializeResponse =
  import('~/store/diia/types.ts').TDiiaInitializeResponse;

export type TFilesSigningDiiaStatusResponse = {
  status: import('~/store/diia/types.ts').DiiaStatuses | string;
};

export type TGuaranteesPageConfiguration = {
  page: number;
  size: number;
  totalElements: number;
};

export type TGuaranteesSpringPageResponse<T> = {
  content?: T[];
  totalElements?: number;
  number?: number;
  size?: number;
};

export type TGuaranteesApplicationsResponse = {
  applications: TGuaranteesApplicationRow[];
  count: number;
  pageConfiguration: TGuaranteesPageConfiguration;
};

export type TGuaranteesManagerTasksResponse = {
  tasks: TGuaranteesApplication[];
  count: number;
  pageConfiguration: TGuaranteesPageConfiguration;
};

export type TGuaranteesSupervisorSegmentInfo = {
  id: string;
  displayName: string;
};

export type TGuaranteesSegmentSupervisor = {
  id: string;
  email: string;
  supervisorEmail?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type TGuaranteesSupervisorGroupKey = 'usapok' | 'uo' | 'umvn' | 'udbtf' | 'uakdo';

export type TGuaranteesSupervisorSegmentsByGroup = Record<
  TGuaranteesSupervisorGroupKey,
  TGuaranteesAsyncSlice<TGuaranteesSupervisorSegment[]>
>;

export type TFetchGuaranteesSupervisorSegmentsByGroupRequest = {
  groupKey: TGuaranteesSupervisorGroupKey;
  email?: string;
};

export type TGuaranteesManagerSupervisorInfoResponse = {
  managerId: string;
  hasPmAccess: boolean;
  hasAmAccess: boolean;
};

export type TGuaranteesAdministrationSupervisorInfoResponse = {
  hasAccess: boolean;
};

export type TGuaranteesUakdoSupervisorInfoResponse = {
  hasResponsibleAccess: boolean;
  hasControllerAccess: boolean;
};

export type TGuaranteesAdministrationSupervisorInfoByPage = Record<
  'usapok' | 'umvn' | 'uo' | 'udbtf',
  TGuaranteesAsyncSlice<TGuaranteesAdministrationSupervisorInfoResponse | null>
>;

export type TGuaranteesSupervisorSegment = {
  segment: TGuaranteesSupervisorSegmentInfo;
  supervisors: TGuaranteesSegmentSupervisor[];
};

export type TCreateGuaranteesSupervisorSegmentRequest = {
  segmentId: string;
  supervisorEmail: string;
  groupKey?: TGuaranteesSupervisorGroupKey;
  email?: string;
};

export type TDeleteGuaranteesSupervisorSegmentSupervisorRequest = {
  segmentId: string;
  supervisorId: string;
  groupKey?: TGuaranteesSupervisorGroupKey;
  email?: string;
};

export enum GuaranteesApplicationRouteId {
  New = 'new',
}

export enum GuaranteesApplicationStatus {
  DRAFT = 'DRAFT',
  AWAITING_CLIENT_INITIAL_SIGN = 'AWAITING_CLIENT_INITIAL_SIGN',
  IN_PROGRESS_CLIENT_INITIAL_SIGN = 'IN_PROGRESS_CLIENT_INITIAL_SIGN',
  AWAITING_AM_APPROVAL_LETTER = 'AWAITING_AM_APPROVAL_LETTER',
  IN_PROGRESS_AM_APPROVAL_LETTER = 'IN_PROGRESS_AM_APPROVAL_LETTER',
  AWAITING_PM_MANAGER_TERMS = 'AWAITING_PM_MANAGER_TERMS',
  IN_PROGRESS_PM_MANAGER_TERMS = 'IN_PROGRESS_PM_MANAGER_TERMS',
  AWAITING_UDBTF_ISSUANCE_TERMS = 'AWAITING_UDBTF_ISSUANCE_TERMS',
  IN_PROGRESS_UDBTF_ISSUANCE_TERMS = 'IN_PROGRESS_UDBTF_ISSUANCE_TERMS',
  AWAITING_INTERNAL_APPROVALS = 'AWAITING_INTERNAL_APPROVALS',
  IN_PROGRESS_INTERNAL_APPROVALS = 'IN_PROGRESS_INTERNAL_APPROVALS',
  AWAITING_CLIENT_APPROVAL_LETTER_SIGN = 'AWAITING_CLIENT_APPROVAL_LETTER_SIGN',
  IN_PROGRESS_CLIENT_APPROVAL_LETTER_SIGN = 'IN_PROGRESS_CLIENT_APPROVAL_LETTER_SIGN',
  AWAITING_AM_APPROVAL_LETTER_SENT_MANUAL_SIGN = 'AWAITING_AM_APPROVAL_LETTER_SENT_MANUAL_SIGN',
  IN_PROGRESS_AM_APPROVAL_LETTER_SENT_MANUAL_SIGN = 'IN_PROGRESS_AM_APPROVAL_LETTER_SENT_MANUAL_SIGN',
  AWAITING_UAKDO_APPROVAL = 'AWAITING_UAKDO_APPROVAL',
  IN_PROGRESS_UAKDO_APPROVAL = 'IN_PROGRESS_UAKDO_APPROVAL',
  AWAITING_UAKDO_CONTROLLER_APPROVAL = 'AWAITING_UAKDO_CONTROLLER_APPROVAL',
  IN_PROGRESS_UAKDO_CONTROLLER_APPROVAL = 'IN_PROGRESS_UAKDO_CONTROLLER_APPROVAL',
  AWAITING_UDBTF_APPROVAL = 'AWAITING_UDBTF_APPROVAL',
  IN_PROGRESS_UDBTF_APPROVAL = 'IN_PROGRESS_UDBTF_APPROVAL',
  AWAITING_GUARANTEE_ISSUANCE = 'AWAITING_GUARANTEE_ISSUANCE',
  IN_PROGRESS_GUARANTEE_ISSUANCE = 'IN_PROGRESS_GUARANTEE_ISSUANCE',
  AWAITING_USAPOK_APPROVAL = 'AWAITING_USAPOK_APPROVAL',
  IN_PROGRESS_USAPOK_APPROVAL = 'IN_PROGRESS_USAPOK_APPROVAL',
  AWAITING_USAPOK_CONTROLLER_APPROVAL = 'AWAITING_USAPOK_CONTROLLER_APPROVAL',
  IN_PROGRESS_USAPOK_CONTROLLER_APPROVAL = 'IN_PROGRESS_USAPOK_CONTROLLER_APPROVAL',
  ISSUED = 'ISSUED',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED',
}

export type TGuaranteesApplicationRow = {
  id: string;
  applicationNumber: string;
  isTemporaryApplicationNumber: boolean;
  tenderId: string;
  createdAt: string;
  guaranteeAmount: number;
  status: GuaranteesApplicationStatus;
};

export type TGuaranteesTimelineEvent = {
  changedAt?: string;
  applicationId?: string;
  description?: string;
  changedByName?: string;
  changedByEmail?: string;
  sequenceNumber?: number;
  statusFrom?: GuaranteesApplicationStatus | string;
  statusTo?: GuaranteesApplicationStatus | string;
};

export type TGuaranteesDocument = {
  documentType?: string;
  fileId?: string;
  fileName?: string;
  mimeType?: string;
  sizeBytes?: number;
  version?: number;
  status?: string;
  generatedAt?: string;
  signedAt?: string;
};

export enum GuaranteeOperationType {
  TENDER = 'TENDER',
  TENDER_PROZORRO = 'TENDER_PROZORRO',
  PERFORMANCE = 'PERFORMANCE',
  PERFORMANCE_PROZORRO = 'PERFORMANCE_PROZORRO',
  ADVANCE_PAYMENT = 'ADVANCE_PAYMENT',
  PAYMENT = 'PAYMENT',
  OTHER = 'OTHER',
}

export enum GuaranteeSecurityType {
  NO_SECURITY = 'NO_SECURITY',
  COVERAGE_ACCOUNT = 'COVERAGE_ACCOUNT',
  DEPOSIT_RIGHTS = 'DEPOSIT_RIGHTS',
}

export type TRootApplicationSectionDto = {
  guaranteeOperationType?: GuaranteeOperationType | null;
  guaranteeOperationTypeOther?: string | null;
  guaranteeAmount?: number | null;
  guaranteeCurrency?: string | null;
  guaranteeExpiryDate?: string | null;
  securityType?: GuaranteeSecurityType | string | null;
  securityAmount?: number | string | null;
  depositAccountIban?: string | null;
  coverageAccountIban?: string | null;
  formationDate?: string | null;
  prozorroTenderId?: string | null;
  tenderPublicId?: string | null;
  mainDealId?: string | null;
  mainDealNumber?: string | null;
  mainDealDate?: string | null;
  mainDealDateFrom?: string | null;
  mainDealDateTo?: string | null;
  mainDealCurrency?: string | null;
  mainDealAmount?: string | null;
  mainDealLimitBalance?: string | null;
  checkAccountsRestrictions?: boolean | null;
  isUrgent?: boolean | null;
};

export type TGuaranteesRootSection = TRootApplicationSectionDto & {
  guarantee_operation_type?: GuaranteeOperationType | null;
  guarantee_operation_type_other?: string | null;
  guarantee_amount?: number | null;
  guarantee_currency?: string | null;
  guarantee_expiry_date?: string | null;
  security_type?: GuaranteeSecurityType | string | null;
  security_amount?: number | string | null;
  deposit_account_iban?: string | null;
  coverage_account_iban?: string | null;
};

export type TGuaranteesPrincipalSection = {
  identifyCode?: string | null;
  fullName?: string | null;
  shortName?: string | null;
  location?: string | null;
  address?: string | null;
  nameAddressGuaranteeLang?: string | null;
  displayNameInGuaranteeLanguage?: string | null;
  contactFullName?: string | null;
  contactPersonFullName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  authorizedPersonPosition?: string | null;
  authorizedPersonName?: string | null;
  authorizedPersonFullName?: string | null;
  hasChangesInConstituentDocuments?: boolean | null;
};

export type TGuaranteesPrincipal = {
  principal_identify_code: string;
  principal_full_name: string;
  principal_short_name: string;
  principal_location: string;
  principal_name_address_guarantee_lang: string;
  principal_contact_full_name: string;
  principal_contact_email: string;
  principal_contact_phone: string;
  principal_authorized_person_position: string;
  principal_authorized_person_name: string;
  hasChangesInConstituentDocuments: TAutocompleteOption | null;
};

export type TPatchGuaranteesPrincipalPayload = {
  identifyCode: string;
  fullName: string;
  shortName: string;
  location: string;
  nameAddressGuaranteeLang: string;
  contactFullName: string;
  contactEmail: string;
  contactPhone: string;
  authorizedPersonPosition: string;
  authorizedPersonName: string;
  hasChangesInConstituentDocuments: boolean;
};

export enum GuaranteesBeneficiaryFieldName {
  BeneficiaryName = 'name',
  BeneficiaryIdentifyCode = 'identifyCode',
  BeneficiaryLocation = 'address',
  BeneficiaryNameGuaranteeLang = 'displayNameInGuaranteeLanguage',
  BeneficiaryGuarantorBank = 'guarantorBank',
  BeneficiaryBank = 'beneficiaryBank',
  AdvisingBank = 'advisingBank',
  IsNonResidentBeneficiary = 'isNonResidentBeneficiary',
  BeneficiaryResidenceCountry = 'beneficiaryResidenceCountry',
}

export type TGuaranteesBeneficiary = {
  [GuaranteesBeneficiaryFieldName.BeneficiaryName]: string;
  [GuaranteesBeneficiaryFieldName.BeneficiaryIdentifyCode]: string;
  [GuaranteesBeneficiaryFieldName.BeneficiaryLocation]: string;
  [GuaranteesBeneficiaryFieldName.BeneficiaryNameGuaranteeLang]: string;
  [GuaranteesBeneficiaryFieldName.BeneficiaryGuarantorBank]: string;
  [GuaranteesBeneficiaryFieldName.BeneficiaryBank]: string;
  [GuaranteesBeneficiaryFieldName.AdvisingBank]: string;
  [GuaranteesBeneficiaryFieldName.IsNonResidentBeneficiary]: boolean;
  [GuaranteesBeneficiaryFieldName.BeneficiaryResidenceCountry]: TAutocompleteOption | null;
  countryName?: string | null;
  countryId?: string | number | null;
};

export type TGuaranteesBeneficiaryResponse = TGuaranteesBeneficiary[];

export type TPatchGuaranteesBeneficiaryPayload = {
  name: string;
  identifyCode: string;
  location: string;
  nameGuaranteeLang: string;
  guarantorBank: string;
  bank: string;
  advisingBank: string;
  countryId: string;
  countryName: string;
};

export enum GuaranteesFormFieldName {
  IssuePlace = 'issuePlace',
  IssueDate = 'issueDate',
  DocumentsLanguage = 'documentsLanguage',
  DemandPresentationForm = 'demandPresentationForm',
  DemandPresentationPlace = 'demandPresentationPlace',
  SupportingDocuments = 'supportingDocuments',
  DeliveryMethod = 'deliveryMethod',
  DeliveryMethodOther = 'deliveryMethodOther',
  GuaranteeFormRule = 'guaranteeFormRule',
  GuaranteeFormRuleOther = 'guaranteeFormRuleOther',
  BankCommissionsRule = 'bankCommissionsRule',
  BankCommissionsRuleOther = 'bankCommissionsRuleOther',
  SubordinationRule = 'subordinationRule',
  SubordinationRuleOther = 'subordinationRuleOther',
  EffectiveFromType = 'effectiveFromType',
  EffectiveFromDate = 'effectiveFromDate',
}

export enum GuaranteesEffectiveFromType {
  ISSUE_DATE = 'ISSUE_DATE',
  OTHER_DATE = 'OTHER_DATE',
}

export enum GuaranteesDeliveryMethod {
  PAPER_ORIGINAL = 'PAPER_ORIGINAL',
  SWIFT = 'SWIFT',
  NBU_EMAIL = 'NBU_EMAIL',
  PRINCIPAL_EMAIL = 'PRINCIPAL_EMAIL',
  OTHER = 'OTHER',
}

export enum GuaranteesFormRule {
  APPLICATION_P7 = 'APPLICATION_P7',
  OTHER = 'OTHER',
}

export enum GuaranteesBankCommissionsRule {
  PRINCIPAL_PAYS_ALL = 'PRINCIPAL_PAYS_ALL',
  DEBIT_PRINCIPAL_ACCOUNT = 'DEBIT_PRINCIPAL_ACCOUNT',
  OTHER = 'OTHER',
}

export enum GuaranteesSubordinationRule {
  UKRAINIAN_LAW = 'UKRAINIAN_LAW',
  URDG_758 = 'URDG_758',
  OTHER = 'OTHER',
}

export type TGuaranteesForm = {
  issuePlace?: string | null;
  issueDate?: string | null;
  documentLanguage?: string | null;
  demandPresentationFormat?: string | null;
  demandPresentationPlace?: string | null;
  customGuaranteeText?: string | null;
  supportingDocuments?: string | null;
  deliveryMethod?: GuaranteesDeliveryMethod | string | null;
  deliveryMethodOther?: string | null;
  guaranteeFormRule?: GuaranteesFormRule | string | null;
  guaranteeFormRuleOther?: string | null;
  bankCommissionsRule?: GuaranteesBankCommissionsRule | string | null;
  bankCommissionsRuleOther?: string | null;
  subordinationRule?: GuaranteesSubordinationRule | string | null;
  subordinationRuleOther?: string | null;
  effectiveFromType?: GuaranteesEffectiveFromType | string | null;
  effectiveFromDate?: string | null;
};

export type TGuaranteesFormValues = {
  [GuaranteesFormFieldName.IssuePlace]: string;
  [GuaranteesFormFieldName.IssueDate]: string;
  [GuaranteesFormFieldName.DocumentsLanguage]: string;
  [GuaranteesFormFieldName.DemandPresentationForm]: string;
  [GuaranteesFormFieldName.DemandPresentationPlace]: string;
  [GuaranteesFormFieldName.SupportingDocuments]: string;
  [GuaranteesFormFieldName.DeliveryMethod]: TAutocompleteOption | null;
  [GuaranteesFormFieldName.DeliveryMethodOther]: string;
  [GuaranteesFormFieldName.GuaranteeFormRule]: TAutocompleteOption | null;
  [GuaranteesFormFieldName.GuaranteeFormRuleOther]: string;
  [GuaranteesFormFieldName.BankCommissionsRule]: TAutocompleteOption | null;
  [GuaranteesFormFieldName.BankCommissionsRuleOther]: string;
  [GuaranteesFormFieldName.SubordinationRule]: TAutocompleteOption | null;
  [GuaranteesFormFieldName.SubordinationRuleOther]: string;
  [GuaranteesFormFieldName.EffectiveFromType]: TAutocompleteOption | null;
  [GuaranteesFormFieldName.EffectiveFromDate]: string;
};

export type TPatchGuaranteesFormPayload = {
  issueDate?: string;
  issuePlace?: string;
  documentsLanguage?: string;
  demandPresentationForm?: string;
  demandPresentationPlace?: string;
  supportingDocuments?: string;
  deliveryMethod: GuaranteesDeliveryMethod;
  deliveryMethodOther: string | null;
  guaranteeFormRule: GuaranteesFormRule;
  guaranteeFormRuleOther: string | null;
  bankCommissionsRule: GuaranteesBankCommissionsRule;
  bankCommissionsRuleOther: string | null;
  subordinationRule?: GuaranteesSubordinationRule;
  subordinationRuleOther: string | null;
  effectiveFromType?: GuaranteesEffectiveFromType;
  effectiveFromDate: string | null;
};

export enum GuaranteesBusinessCategory {
  CORPORATE = 'CORPORATE',
  SMB = 'SMB',
}

export enum GuaranteesRiskApprovalStatus {
  APPROVED = 'APPROVED',
  NOT_APPROVED = 'NOT_APPROVED',
}

export enum GuaranteesContractName {
  GENERAL_GUARANTEE_AGREEMENT = 'GENERAL_GUARANTEE_AGREEMENT',
  ADDITIONAL_GUARANTEE_AGREEMENT = 'ADDITIONAL_GUARANTEE_AGREEMENT',
  GUARANTEE_AGREEMENT = 'GUARANTEE_AGREEMENT',
}

export enum GuaranteesMainDealConditionsStatus {
  COMPLIED = 'COMPLIED',
  NOT_COMPLIED = 'NOT_COMPLIED',
}

export enum GuaranteesMainDealConditionsFieldName {
  BusinessCategory = 'businessCategory',
  RiskApprovalStatus = 'riskApprovalStatus',
  ContractName = 'contractName',
  MaxGuaranteeTermDays = 'maxGuaranteeTermDays',
  MainDealConditionsStatus = 'mainDealConditionsStatus',
  NormativeCoverageRatio = 'normativeCoverageRatio',
}

export type TGuaranteesMainDealConditions = {
  version?: number;
  businessCategory?: GuaranteesBusinessCategory | string | null;
  riskApprovalStatus?: GuaranteesRiskApprovalStatus | string | null;
  contractName?: GuaranteesContractName | string | null;
  maxGuaranteeTermDays?: number | string | null;
  mainDealConditionsStatus?: GuaranteesMainDealConditionsStatus | string | null;
  normativeCoverageRatio?: string | null;
};

export type TGuaranteesMainDealConditionsValues = {
  [GuaranteesMainDealConditionsFieldName.BusinessCategory]: string;
  [GuaranteesMainDealConditionsFieldName.RiskApprovalStatus]: TAutocompleteOption | null;
  [GuaranteesMainDealConditionsFieldName.ContractName]: TAutocompleteOption | null;
  [GuaranteesMainDealConditionsFieldName.MaxGuaranteeTermDays]: string;
  [GuaranteesMainDealConditionsFieldName.MainDealConditionsStatus]: TAutocompleteOption | null;
  [GuaranteesMainDealConditionsFieldName.NormativeCoverageRatio]: string;
};

export type TPatchGuaranteesMainDealConditionsPayload = {
  riskApprovalStatus: GuaranteesRiskApprovalStatus;
  contractName: GuaranteesContractName;
  maxGuaranteeTermDays: number;
  mainDealConditionsStatus: GuaranteesMainDealConditionsStatus;
  normativeCoverageRatio: string;
};

export enum GuaranteesBaseRelationsFieldName {
  RelationsDocument = 'relationsDocument',
  PrincipalObligationDetails = 'principalObligationDetails',
}

export enum GuaranteesPrincipalObligationDetails {
  PRINCIPAL_NOT_FULFILLED = 'PRINCIPAL_NOT_FULFILLED',
  PRINCIPAL_IMPROPERLY_FULFILLED = 'PRINCIPAL_IMPROPERLY_FULFILLED',
  CONTRACT_TERMINATED_DUE_TO_PRINCIPAL_ACTIONS = 'CONTRACT_TERMINATED_DUE_TO_PRINCIPAL_ACTIONS',
}

export type TGuaranteesBaseRelationsSection = {
  baseRelationsDocument?: string | null;
  principalObligationDetails?: GuaranteesPrincipalObligationDetails | string | null;
  relationsDocument?: string | null;
  base_relations_document?: string | null;
  baseDocumentName?: string | null;
  baseDocumentNumber?: string | null;
  baseDocumentDate?: string | null;
  baseDocumentSubject?: string | null;
  baseDocumentAmount?: string | number | null;
  baseDocumentCurrency?: string | null;
};

export type TGuaranteesBaseRelations = TGuaranteesBaseRelationsSection;

export type TGuaranteesBaseRelationsValues = {
  [GuaranteesBaseRelationsFieldName.RelationsDocument]: string;
  [GuaranteesBaseRelationsFieldName.PrincipalObligationDetails]: TAutocompleteOption | null;
};

export type TPatchGuaranteesBaseRelationsPayload = {
  baseRelationsDocument: string;
  principalObligationDetails: GuaranteesPrincipalObligationDetails;
};

export type TGuaranteesTermsSection = {
  guaranteeAmount?: string | number | null;
  guaranteeCurrency?: string | null;
  guaranteeExpiryDate?: string | null;
  guaranteeIssueDate?: string | null;
  mainDealNumber?: string | null;
  mainDealDate?: string | null;
};

export type TGuaranteesSecuritySectionDto = {
  type?: string | null;
  amount?: number | string | null;
  securityType?: string | null;
  securityAmount?: string | number | null;
  coverageAmount?: string | null;
  depositAccountIban?: string | null;
  coverageAccountIban?: string | null;
  currencies?: string[] | null;
  requiredSecurityAmount?: string | null;
  pledgorName?: string | null;
  pledgorHasChangesInConstituentDocuments?: boolean | null;
  pledgorIdentifyCode?: string | null;
  principalDebitAccountIban?: string | null;
};

export type TGuaranteesNamedValueSection = Record<string, unknown>;

export enum GuaranteesBeneficiarySanctionsStatus {
  PRESENT = 'PRESENT',
  ABSENT = 'ABSENT',
}

export type TGuaranteesManagerTerms = {
  version?: number;
  guaranteeExpiryDate?: string | null;
  beneficiarySanctionsMatchDetected?: boolean | null;
  beneficiarySanctionsStatus?: GuaranteesBeneficiarySanctionsStatus | string | null;
  debtType?: string | null;
};

export enum GuaranteesExecutorMainDealConditionsStatus {
  PERFORMED = 'PERFORMED',
  NOT_PERFORMED = 'NOT_PERFORMED',
}

export enum GuaranteesAccountPlacementStatus {
  PLACED_FULLY = 'PLACED_FULLY',
  PLACED_PARTIALLY = 'PLACED_PARTIALLY',
  NOT_PLACED = 'NOT_PLACED',
}

export type TGuaranteesExecutionForm = {
  version?: number;
  unfulfilledConditionsDetails?: string | null;
  depositAccountPlacementStatus?: GuaranteesAccountPlacementStatus | string | null;
  coverageAccountPlacementStatus?: GuaranteesAccountPlacementStatus | string | null;
  executorMainDealConditionsStatus?: GuaranteesExecutorMainDealConditionsStatus | string | null;
  mainDealConditionsStatus?: string | null;
  applicationType?: string | null;
  applicationForm?: string | null;
};

export type TPatchGuaranteesExecutionFormPayload = {
  unfulfilledConditionsDetails: string;
  depositAccountPlacementStatus?: GuaranteesAccountPlacementStatus;
  coverageAccountPlacementStatus?: GuaranteesAccountPlacementStatus;
  executorMainDealConditionsStatus: GuaranteesExecutorMainDealConditionsStatus;
};

export type TPatchGuaranteesManagerTermsPayload = {
  beneficiarySanctionsStatus: GuaranteesBeneficiarySanctionsStatus | null;
};

export type TGuaranteesSecurityCurrenciesPayload = {
  currencies: string[];
};

export type TGuaranteesSecurityCurrenciesResponse = TGuaranteesSecurityCurrenciesPayload;

export type TPatchGuaranteesRootApplicationSectionRequest = {
  contragentId: string;
  applicationId: string;
  version: number;
  payload: TRootApplicationSectionDto;
};

export type TPatchGuaranteesPrincipalRequest = {
  contragentId: string;
  applicationId: string;
  version: number;
  payload: TPatchGuaranteesPrincipalPayload;
};

export type TPatchGuaranteesBeneficiaryRequest = {
  contragentId: string;
  applicationId: string;
  version: number;
  payload: TPatchGuaranteesBeneficiaryPayload;
};

export type TPatchGuaranteesFormRequest = {
  contragentId: string;
  applicationId: string;
  version: number;
  payload: TPatchGuaranteesFormPayload;
};

export type TPatchGuaranteesMainDealConditionsRequest = {
  contragentId: string;
  applicationId: string;
  version: number;
  payload: TPatchGuaranteesMainDealConditionsPayload;
};

export type TPatchGuaranteesBaseRelationsRequest = {
  contragentId: string;
  applicationId: string;
  version: number;
  payload: TPatchGuaranteesBaseRelationsPayload;
};

export type TPatchGuaranteesManagerTermsRequest = {
  contragentId: string;
  applicationId: string;
  version: number;
  payload: TPatchGuaranteesManagerTermsPayload;
};

export type TPatchGuaranteesExecutionFormRequest = {
  applicationId: string;
  version: number;
  payload: TPatchGuaranteesExecutionFormPayload;
};

export type TFetchGuaranteesExecutionFormRequest = {
  applicationId: string;
};

export type TPatchGuaranteesApplicationTenderRequest = {
  contragentId: string;
  applicationId: string;
  version: number;
  tenderPublicId: string;
  selectedLotIds: string[];
};

export type TGuaranteesApplicationSelectionPatchResult = {
  application: TGuaranteesApplication | null;
  status: number;
};

export type TFetchGuaranteesApplicationTenderInfoRequest = {
  contragentId: string;
  applicationId: string;
};

export type TGuaranteesApplicationTenderLot = {
  id?: string | null;
};

export type TGuaranteesApplicationTenderLotsPayload = {
  tender: {
    lots: { id: string }[];
  };
};

export type TGuaranteesApplicationTenderInfo = {
  tender?: {
    lots?: TGuaranteesApplicationTenderLot[] | null;
  } | null;
  lotIds?: string[] | null;
};

export type TPatchGuaranteesApplicationMainDealRequest = {
  contragentId: string;
  applicationId: string;
  version: number;
  mainDealId: string;
};

export type TPatchGuaranteesApplicationSecretRequest = {
  contragentId: string;
  applicationId: string;
  version: number;
  payload: TGuaranteesSecuritySectionDto;
};

export type TFetchGuaranteesSecurityCurrenciesRequest = {
  contragentId: string;
  applicationId: string;
};

export type TPutGuaranteesSecurityCurrenciesRequest = {
  contragentId: string;
  applicationId: string;
  version: number;
  payload: TGuaranteesSecurityCurrenciesPayload;
};

export type TCheckGuaranteesApplicationRequest = {
  contragentId: string;
  applicationId: string;
  version: number;
  checkAccountRestriction: boolean;
};

export type TFetchGuaranteesProzorroTenderRequest = {
  tenderPublicId: string;
};

export type TFetchGuaranteesMainDealsRequest = {
  contragentId: string;
  page?: number;
  size?: number;
  sort?: string[];
};

export type TFetchGuaranteesAccountsRequest = {
  contragentId: string;
  isPrincipalDebitAccount: boolean;
};

export type TGuaranteesProzorroTender = {
  id?: number | string | null;
  tenderPublicId?: string | null;
  tenderInternalId?: string | null;
  dateModified?: string | null;
  status?: string | null;
  firstSeenAt?: string | null;
  lastSeenAt?: string | null;
  lastSyncedAt?: string | null;
  rawFeedJson?: string | null;
};

export type TGuaranteesMainDeal = {
  id?: number | string | null;
  dealId?: number | string | null;
  mainDealId?: number | string | null;
  dealNumber?: string | null;
  mainDealDate?: string | null;
  mainDealDateFrom?: string | null;
  mainDealDateTo?: string | null;
  mainDealCurrency?: string | null;
  mainDealAmount?: number | string | null;
  mainDealLimitBalance?: number | string | null;
};

export type TGuaranteesMainDealsResponse = {
  content?: TGuaranteesMainDeal[];
  totalPages?: number;
  totalElements?: number;
  number?: number;
  size?: number;
  last?: boolean;
};

export enum GuaranteesAccountType {
  COVERAGE = 'COVERAGE',
  DEPOSIT = 'DEPOSIT',
}

export type TGuaranteesAccount = {
  iban?: string | null;
  frozen?: boolean | null;
  ourBank?: boolean | null;
  type?: GuaranteesAccountType | (string & {}) | null;
};

export type TGuaranteesAccountsResponse = {
  content?: TGuaranteesAccount[];
  totalPages?: number;
  totalElements?: number;
  number?: number;
  size?: number;
  last?: boolean;
};

export type TInternalApprovalStatusField =
  | 'udbtfApprovalStatus'
  | 'uoApprovalStatus'
  | 'umvnApprovalStatus';

export type TGuaranteesApplication = {
  id: string;
  applicationId?: string;
  localId?: string | number | null;
  contragentId?: string;
  version?: number;
  managerId?: string;
  managerEmail?: string;
  amManagerId?: string | null;
  pmManagerId?: string | null;
  assignedEmployeeEmail?: string | null;
  udbtfAssignedEmployeeEmail?: string | null;
  umvnAssignedEmployeeEmail?: string | null;
  uoAssignedEmployeeEmail?: string | null;
  inProgressEmployeeEmail?: string | null;
  udbtfInProgressEmployeeEmail?: string | null;
  umvnInProgressEmployeeEmail?: string | null;
  uoInProgressEmployeeEmail?: string | null;
  applicationNumber?: string;
  createdAt?: string;
  updatedAt?: string;
  applicationStatus?: string;
  applicationStatusChangedAt?: string;
  processingDeadline?: string | null;
  processingOverdue?: boolean | null;
  udbtfProcessingDeadline?: string | null;
  udbtfProcessingOverdue?: boolean | null;
  umvnProcessingDeadline?: string | null;
  umvnProcessingOverdue?: boolean | null;
  uoProcessingDeadline?: string | null;
  uoProcessingOverdue?: boolean | null;
  udbtfApprovalStatus?: GuaranteesUdbtfApprovalStatus | string | null;
  uoApprovalStatus?: GuaranteesUoApprovalStatus | string | null;
  umvnApprovalStatus?: GuaranteesUmvnApprovalStatus | string | null;
  flowType?: TGuaranteesFlowType;
  baseRelationsDocument?: string | null;
  principalObligationDetails?: GuaranteesPrincipalObligationDetails | string | null;
  isUrgent?: boolean | null;
  legalApprovalRequired?: boolean;
  umvnApprovalRequired?: boolean;
  principalShortName?: string;
  root?: Record<string, unknown> & TGuaranteesRootSection;
  principal?: Record<string, unknown> & TGuaranteesPrincipalSection;
  beneficiary?: TGuaranteesBeneficiary | TGuaranteesBeneficiaryResponse;
  baseRelations?: Record<string, unknown> & TGuaranteesBaseRelationsSection;
  guaranteeTerms?: Record<string, unknown> & TGuaranteesTermsSection;
  security?: Record<string, unknown> & TGuaranteesSecuritySectionDto;
  guaranteeForm?: TGuaranteesForm;
  mainDealConditions?: TGuaranteesMainDealConditions;
  managerTerms?: TGuaranteesManagerTerms;
  executionForm?: TGuaranteesExecutionForm | TGuaranteesNamedValueSection;
  issuanceTerms?: TGuaranteesNamedValueSection;
  issueDetails?: TGuaranteesIssueGuaranteeDetails;
  managementAccounting?: TGuaranteesManagementAccounting;
  executionCheck?: GuaranteesApprovalLetterExecutionCheck | string | null;
  executionChecks?: {
    applicationForm?: GuaranteesApprovalLetterExecutionCheck | string | null;
    executionCheck?: GuaranteesApprovalLetterExecutionCheck | string | null;
  } & TGuaranteesNamedValueSection;
  approvalLetter?: Record<string, unknown> & {
    document?: TGuaranteesDocument;
  };
  documents?: {
    applicationDocument?: TGuaranteesDocument;
    approvalLetterDocument?: TGuaranteesDocument;
    uoSummaryDocument?: TGuaranteesDocument;
    umvnSummaryDocument?: TGuaranteesDocument;
  };
  attachments?: Array<{
    attachmentId?: string;
    fileId?: string;
    fileName?: string;
    attachmentType?: string;
    comment?: string;
    uploadedAt?: string;
    uploadedBy?: string;
  }>;
};

export const guaranteesApplicationStatuses = Object.values(GuaranteesApplicationStatus);

export type TGuaranteesPageStore = {
  applications: TGuaranteesAsyncSlice<TGuaranteesApplicationsResponse>;
  applicationTimeline: TGuaranteesAsyncSlice<TGuaranteesTimelineEvent[]>;
  applicationFileCapabilities: TGuaranteesAsyncSlice<TGuaranteesApplicationFileCapabilitiesResponse>;
  applicationFileTypeCapabilities: TGuaranteesAsyncSlice<TGuaranteesApplicationFileCapabilitiesResponse>;
  uploadApplicationFile: TGuaranteesAsyncSlice<TUploadGuaranteesApplicationFileResponse | null>;
  regenerateApplicationFile: TGuaranteesAsyncSlice<TRegenerateGuaranteesApplicationFileResponse | null>;
  patchApplicationFileCapabilities: TGuaranteesAsyncSlice<TPatchGuaranteesApplicationFileCapabilitiesResponse | null>;
  downloadApplicationFile: TGuaranteesAsyncSlice<TDownloadGuaranteesApplicationFileResponse | null>;
  deleteApplicationFile: TGuaranteesAsyncSlice<void | null>;
  createApplication: TGuaranteesAsyncSlice<TGuaranteesApplication | null>;
  currentApplication: TGuaranteesAsyncSlice<TGuaranteesApplication | null>;
  patchRootApplicationSection: TGuaranteesAsyncSlice<TGuaranteesApplication | null>;
  principal: TGuaranteesAsyncSlice<
    (Record<string, unknown> & TGuaranteesPrincipalSection) | TGuaranteesPrincipal | null
  >;
  patchPrincipal: TGuaranteesAsyncSlice<TGuaranteesPrincipal | TGuaranteesApplication | null>;
  beneficiary: TGuaranteesAsyncSlice<TGuaranteesBeneficiaryResponse>;
  patchBeneficiary: TGuaranteesAsyncSlice<TGuaranteesBeneficiaryResponse | TGuaranteesApplication | null>;
  baseRelations: TGuaranteesAsyncSlice<TGuaranteesBaseRelations | null>;
  patchBaseRelations: TGuaranteesAsyncSlice<TGuaranteesBaseRelations | TGuaranteesApplication | null>;
  guaranteeForm: TGuaranteesAsyncSlice<TGuaranteesForm | null>;
  patchGuaranteeForm: TGuaranteesAsyncSlice<TGuaranteesForm | TGuaranteesApplication | null>;
  mainDealConditions: TGuaranteesAsyncSlice<TGuaranteesMainDealConditions | null>;
  patchMainDealConditions: TGuaranteesAsyncSlice<TGuaranteesMainDealConditions | null>;
  managerTerms: TGuaranteesAsyncSlice<TGuaranteesManagerTerms | null>;
  patchManagerTerms: TGuaranteesAsyncSlice<TGuaranteesManagerTerms | TGuaranteesApplication | null>;
  executionForm: TGuaranteesAsyncSlice<TGuaranteesExecutionForm | null>;
  patchExecutionForm: TGuaranteesAsyncSlice<TGuaranteesExecutionForm | TGuaranteesApplication | null>;
  patchIssueGuarantee: TGuaranteesAsyncSlice<TPatchGuaranteesIssueGuaranteeResponse>;
  patchApplicationTender: TGuaranteesAsyncSlice<TGuaranteesApplication | null>;
  applicationTenderInfo: TGuaranteesAsyncSlice<TGuaranteesApplicationTenderInfo | null>;
  fetchProzorroTender: TGuaranteesAsyncSlice<TGuaranteesProzorroTender | null>;
  patchApplicationMainDeal: TGuaranteesAsyncSlice<TGuaranteesApplication | null>;
  mainDeals: TGuaranteesAsyncSlice<TGuaranteesMainDealsResponse>;
  mainDealsRequestKey: string | null;
  accounts: TGuaranteesAsyncSlice<TGuaranteesAccountsResponse>;
  principalDebitAccounts: TGuaranteesAsyncSlice<TGuaranteesAccountsResponse>;
  securityCurrencies: TGuaranteesAsyncSlice<TGuaranteesSecurityCurrenciesResponse | null>;
  putSecurityCurrencies: TGuaranteesAsyncSlice<TGuaranteesApplication | null>;
  udbtfTemplateDocument: TGuaranteesAsyncSlice<TGuaranteesUdbtfTemplateDocumentResponse | null>;
  amTemplateDocument: TGuaranteesAsyncSlice<TGuaranteesAmTemplateDocumentResponse | null>;
  conclusionTemplateDocument: TGuaranteesAsyncSlice<
    TGuaranteesConclusionTemplateDocumentResponse | null
  >;
  approvalComment: TGuaranteesAsyncSlice<TGuaranteesApprovalCommentResponse | null>;
  generateConclusionAttachment: TGuaranteesAsyncSlice<
    TGenerateGuaranteesConclusionAttachmentResponse
  >;
  patchApplicationSecret: TGuaranteesAsyncSlice<TGuaranteesApplication | null>;
  checkApplicationRequest: TGuaranteesAsyncSlice<TGuaranteesApplication | null>;
  managerSupervisorInfo: TGuaranteesAsyncSlice<TGuaranteesManagerSupervisorInfoResponse | null>;
  uakdoSupervisorInfo: TGuaranteesAsyncSlice<TGuaranteesUakdoSupervisorInfoResponse | null>;
  administrationSupervisorInfoByPage: TGuaranteesAdministrationSupervisorInfoByPage;
  managerTasks: TGuaranteesAsyncSlice<TGuaranteesManagerTasksResponse>;
  administrationRoles: TGuaranteesAsyncSlice<TGuaranteesAdministrationRolesResponse>;
  administrationTasks: TGuaranteesAsyncSlice<TGuaranteesAdministrationTasksResponse>;
  updateApplicationStatus: TGuaranteesAsyncSlice<TUpdateGuaranteesApplicationStatusResponse | null>;
  taskSupervisors: TGuaranteesAsyncSlice<TGuaranteesTaskSupervisorsResponse | null>;
  patchApplicationAssignee: TGuaranteesAsyncSlice<TPatchGuaranteesApplicationAssigneeResponse | null>;
  updateUdbtfApprovalStatus: TGuaranteesAsyncSlice<TUpdateGuaranteesUdbtfApprovalStatusResponse>;
  updateUoApprovalStatus: TGuaranteesAsyncSlice<TUpdateGuaranteesUoApprovalStatusResponse>;
  updateUmvnApprovalStatus: TGuaranteesAsyncSlice<TUpdateGuaranteesUmvnApprovalStatusResponse>;
  submitApplicationByClient: TGuaranteesAsyncSlice<TSubmitGuaranteesApplicationByClientResponse>;
  submitApprovalLetterByClient: TGuaranteesAsyncSlice<TSubmitGuaranteesApprovalLetterByClientResponse>;
  submitApplicationByManager: TGuaranteesAsyncSlice<TSubmitGuaranteesApplicationByManagerResponse>;
  submitApplicationByUdbtf: TGuaranteesAsyncSlice<TSubmitGuaranteesApplicationByUdbtfResponse>;
  submitApprovalLetterByUdbtf: TGuaranteesAsyncSlice<TSubmitGuaranteesApprovalLetterByUdbtfResponse>;
  submitApprovalLetterByUsapok: TGuaranteesAsyncSlice<TSubmitGuaranteesApprovalLetterByUsapokResponse>;
  submitApplicationByUakdo: TGuaranteesAsyncSlice<TSubmitGuaranteesApplicationByUakdoResponse>;
  submitApplicationByUakdoController: TGuaranteesAsyncSlice<TSubmitGuaranteesApplicationByUakdoControllerResponse>;
  rejectApplicationByUakdoController: TGuaranteesAsyncSlice<TRejectGuaranteesApplicationByUakdoControllerResponse>;
  submitApplicationByAmManager: TGuaranteesAsyncSlice<TSubmitGuaranteesApplicationByAmManagerResponse>;
  submitApprovalLetterByAmManager: TGuaranteesAsyncSlice<TSubmitGuaranteesApprovalLetterByAmManagerResponse>;
  supervisorSegments: TGuaranteesAsyncSlice<TGuaranteesSupervisorSegment[]>;
  supervisorSegmentsByGroup: TGuaranteesSupervisorSegmentsByGroup;
};
